import React, { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useEntraAuth } from '../components/EntraAuth';

const BlobStorage = () => {
    const { getAccessToken, user } = useEntraAuth();

    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [deletingFileName, setDeletingFileName] = useState('');
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const loadFiles = useCallback(async () => {
        setLoadingFiles(true);
        setError('');

        try {
            const entraAccessToken = await getAccessToken();
            const response = await fetch('/api/storage/my-files', {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${entraAccessToken}`,
                },
            });

            if (!response.ok) {
                const listError = await response.json().catch(() => ({}));
                throw new Error(listError.error || 'Nu am putut încărca lista de fișiere.');
            }

            const data = await response.json();
            setFiles(data.files || []);
        } catch (listError) {
            setError(listError.message || 'A apărut o eroare la listarea fișierelor.');
        } finally {
            setLoadingFiles(false);
        }
    }, [getAccessToken]);

    useEffect(() => {
        loadFiles();
    }, [loadFiles]);

    const handleFileChange = (event) => {
        setError('');
        setSuccess('');
        setSelectedFile(event.target.files?.[0] || null);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Selectați un fișier înainte de încărcare.');
            return;
        }

        setUploading(true);
        setError('');
        setSuccess('');

        try {
            const entraAccessToken = await getAccessToken();

            const sasResponse = await fetch('/api/storage/blob-upload-url', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${entraAccessToken}`,
                },
                body: JSON.stringify({
                    fileName: selectedFile.name,
                    fileType: selectedFile.type || 'application/octet-stream',
                }),
            });

            if (!sasResponse.ok) {
                const sasError = await sasResponse.json().catch(() => ({}));
                throw new Error(sasError.error || 'Nu am putut genera URL-ul de încărcare.');
            }

            const { uploadUrl, requiredHeaders, blobUrl } = await sasResponse.json();

            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                headers: requiredHeaders,
                body: selectedFile,
            });

            if (!uploadResponse.ok) {
                throw new Error('Încărcarea în Azure Blob Storage a eșuat.');
            }

            setSuccess(`Fișier încărcat cu succes: ${selectedFile.name}. URL: ${blobUrl}`);
            setSelectedFile(null);
            await loadFiles();
        } catch (uploadError) {
            setError(uploadError.message || 'A apărut o eroare la încărcare.');
        } finally {
            setUploading(false);
        }
    };

    const formatSize = (size) => {
        if (!size) return '0 B';
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };

    const getDisplayName = (name) => {
        const parts = String(name || '').split('/');
        const leaf = parts[parts.length - 1] || '';
        const dashIndex = leaf.indexOf('-');
        if (dashIndex > 0) {
            return leaf.slice(dashIndex + 1);
        }
        return leaf;
    };

    const handleDelete = async (blobName) => {
        const displayName = getDisplayName(blobName);
        const confirmed = window.confirm(`Sigur doriți să ștergeți fișierul "${displayName}"?`);
        if (!confirmed) {
            return;
        }

        setDeletingFileName(blobName);
        setError('');
        setSuccess('');

        try {
            const entraAccessToken = await getAccessToken();
            const response = await fetch(`/api/storage/my-files/${encodeURIComponent(blobName)}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${entraAccessToken}`,
                },
            });

            if (!response.ok) {
                const deleteError = await response.json().catch(() => ({}));
                throw new Error(deleteError.error || 'Nu am putut șterge fișierul.');
            }

            setSuccess(`Fișier șters: ${displayName}`);
            await loadFiles();
        } catch (deleteError) {
            setError(deleteError.message || 'A apărut o eroare la ștergere.');
        } finally {
            setDeletingFileName('');
        }
    };

    return (
        <div className="sisteme-page">
            <div className="page-navigation-header">
                <div className="nav-header-content">
                    <NavLink to="/dashboard" className="back-to-dashboard">
                        ← Înapoi la Dashboard
                    </NavLink>
                </div>
            </div>

            <div className="page-header">
                <h1>Azure Blob Storage</h1>
                <p>Încărcați fișiere în storage-ul Azure folosind sesiunea Entra activă.</p>
            </div>

            <div className="applications-section">
                <h3 className="section-title">Upload fișiere</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <div style={{ color: '#666' }}>
                        Utilizator autentificat: <strong>{user?.email || user?.name || 'N/A'}</strong>
                    </div>

                    <input type="file" onChange={handleFileChange} disabled={uploading} />

                    <button
                        type="button"
                        onClick={handleUpload}
                        disabled={uploading || !selectedFile}
                        style={{
                            padding: '0.75rem 1rem',
                            borderRadius: '8px',
                            border: 'none',
                            background: uploading || !selectedFile ? '#b9c2d0' : '#667eea',
                            color: 'white',
                            cursor: uploading || !selectedFile ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            width: 'fit-content',
                        }}
                    >
                        {uploading ? 'Se încarcă...' : 'Încarcă în Azure Blob'}
                    </button>

                    {selectedFile && (
                        <div style={{ color: '#333' }}>
                            Fișier selectat: <strong>{selectedFile.name}</strong>
                        </div>
                    )}

                    {error && <div style={{ color: '#dc3545' }}>{error}</div>}
                    {success && <div style={{ color: '#198754' }}>{success}</div>}
                </div>
            </div>

            <div className="applications-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                    <h3 className="section-title" style={{ marginBottom: 0 }}>Fișiere încărcate</h3>
                    <button
                        type="button"
                        onClick={loadFiles}
                        disabled={loadingFiles}
                        style={{
                            padding: '0.5rem 0.8rem',
                            borderRadius: '8px',
                            border: '1px solid #d0d7e2',
                            background: 'white',
                            cursor: loadingFiles ? 'not-allowed' : 'pointer',
                            color: '#334155',
                        }}
                    >
                        {loadingFiles ? 'Actualizare...' : 'Reîncarcă lista'}
                    </button>
                </div>

                <div style={{ marginTop: '1rem' }}>
                    {loadingFiles ? (
                        <div style={{ color: '#666' }}>Se încarcă fișierele...</div>
                    ) : files.length === 0 ? (
                        <div style={{ color: '#666' }}>Nu există fișiere încărcate.</div>
                    ) : (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.75rem' }}>
                            {files.map((file) => (
                                <li
                                    key={file.name}
                                    style={{
                                        border: '1px solid #eef1f5',
                                        borderRadius: '8px',
                                        padding: '0.75rem 1rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: '1rem',
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {getDisplayName(file.name)}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                            {formatSize(file.size)} • {file.lastModified ? new Date(file.lastModified).toLocaleString() : 'N/A'}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <a href={file.url} target="_blank" rel="noreferrer" style={{ color: '#667eea', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                                            Deschide
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(file.name)}
                                            disabled={deletingFileName === file.name}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                color: '#dc3545',
                                                cursor: deletingFileName === file.name ? 'not-allowed' : 'pointer',
                                                whiteSpace: 'nowrap',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {deletingFileName === file.name ? 'Ștergere...' : 'Șterge'}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BlobStorage;
